import { useEffect } from 'react'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import EnhancedTableBodyMyTasks from '../components/EnhancedTableBodyMyTasks'
import { HeadCell } from '@common/components/Table/table.types'
import { Task } from '@common/config/api/client'
import { ROW_PER_PAGE_OPTIONS } from '@common/constants/table'
import { useTabOptionalComponent } from '@common/hooks/useTabOptionalComponent'
import { useParams } from 'react-router-dom'
import { useQCTasksManagementHandlers } from '../hooks/useQCTasksManagementHandlers'
import { Stack, Typography, TableBody, TableRow, TableCell } from '@mui/material'
import { SPACING } from '@common/theme/spacing'
import { ExtendedTask } from './QcTaskListTableCompletedTasks'

interface QcTaskListTableMyTasksProps {
  subjectId?: string
}

const headCells: readonly HeadCell<ExtendedTask>[] = [
  { id: 'task_pseudo_id', disablePadding: true, label: 'Task Pseudo ID', disableSort: false },
  { id: 'sites', disablePadding: false, label: 'Site ID', disableSort: false },
  { id: 'subject_id', disablePadding: false, label: 'Subject ID', disableSort: false },
  { id: 'subject_blind_code', disablePadding: false, label: 'Subject Blind Code', disableSort: true },
  { id: 'timepoint_requirement', disablePadding: false, label: 'Timepoint', disableSort: false },
  { id: 'task_type', disablePadding: false, label: 'Task Type', disableSort: false },
  { id: 'task_state', disablePadding: false, label: 'Task Status', disableSort: false },
  { id: 'query_status', disablePadding: false, label: 'Query Status', disableSort: false },
  { id: 'create_date', disablePadding: false, label: 'Created At', disableSort: false },
]

function EmptyTasksTable() {
  return (
    <Stack spacing={SPACING.minSpacing} alignItems="center" sx={{ py: SPACING.spacing2xl }}>
      <Typography variant="h6">No tasks available</Typography>
      <Typography variant="body2" color="text.secondary">
        There are currently no tasks assigned to you
      </Typography>
    </Stack>
  )
}

export default function QcTaskListTableMyTasks({ subjectId }: QcTaskListTableMyTasksProps) {
  const { studyId } = useParams()
  const { setOptionalComponent, setIsComponentVisible, setIsComponentUsed } = useTabOptionalComponent()

  const {
    order,
    orderBy,
    page,
    paginationParams,
    data,
    error,
    isLoading,
    handleRequestSort,
    handlePageChange,
    handleRowsPerPageChange,
  } = useQCTasksManagementHandlers({
    category: 'my_tasks',
    studyId: studyId!,
    subjectId,
  })

  useEffect(() => {
    setIsComponentVisible(false)
    setIsComponentUsed(false)
    setOptionalComponent(null)
  }, [])

  if (isLoading) {
    return <div>Loading...</div>
  }
  if (error) {
    return <div>Error: {error.message}</div>
  }

  const hasTasks = data?.data?.items && data.data.items.length > 0

  return (
    <EnhancedTable<ExtendedTask>
      rows={data?.data?.items || []}
      headCells={headCells}
      order={order}
      orderBy={orderBy.split('.')[0] as keyof Task}
      rowsPerPage={paginationParams.limit || 0}
      rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
      page={page}
      next_cursor={data?.data?.pagination.next_cursor}
      previous_cursor={data?.data?.pagination.previous_cursor}
      onRequestSort={handleRequestSort}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handleRowsPerPageChange}
    >
      {hasTasks ? (
        <EnhancedTableBodyMyTasks rows={data?.data?.items || []} order={order} orderBy={orderBy} />
      ) : (
        <TableBody>
          <TableRow>
            <TableCell colSpan={headCells.length}>
              <EmptyTasksTable />
            </TableCell>
          </TableRow>
        </TableBody>
      )}
    </EnhancedTable>
  )
}
